import React, { useState } from 'react';
import styled from 'styled-components';


const ExplorerContainer = styled.div`
  padding: 0;
  font-family: 'Ubuntu', sans-serif;
  color: #00ff00;
  background: rgba(0, 0, 0, 0.9);
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Toolbar = styled.div`
  background: rgba(0, 0, 0, 0.8);
  padding: 15px 20px;
  border-bottom: 1px solid rgba(0, 255, 0, 0.3);
  display: flex;
  align-items: center;
  gap: 15px;
`;

const PathBar = styled.div`
  flex: 1;
  background: rgba(0, 0, 0, 0.5);
  padding: 8px 12px;
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  color: #00ffff;
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 25px;
  overflow-y: auto;
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 25px;
`;

const ProjectCard = styled.div`
  background: rgba(0, 255, 0, 0.1);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 12px;
  padding: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    background: rgba(0, 255, 0, 0.2);
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 255, 0, 0.2);
    border-color: #00ff00;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(0, 255, 0, 0.1), transparent);
    transition: left 0.5s ease;
  }

  &:hover::before {
    left: 100%;
  }
`;

const ProjectIcon = styled.div`
  font-size: 40px;
  margin-bottom: 15px;
  text-align: center;
`;

const ProjectTitle = styled.h3`
  color: #00ffff;
  margin-bottom: 12px;
  font-size: 1.5em;
  text-align: center;
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 15px;
  justify-content: center;
`;

const TechTag = styled.span`
  background: rgba(0, 255, 255, 0.2);
  color: #00ffff;
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 12px;
  border: 1px solid rgba(0, 255, 255, 0.3);
`;

const ProjectDescription = styled.div`
  color: #cccccc;
  margin-bottom: 20px;
  line-height: 1.6;
`;

const DescriptionList = styled.ul`
  list-style: none;
  padding: 0;
`;

const DescriptionItem = styled.li`
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
  font-size: 14px;

  &::before {
    content: '▶';
    position: absolute;
    left: 0;
    color: #00ff00;
  }
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
`;

const LinkButton = styled.a`
  background: rgba(0, 255, 0, 0.2);
  border: 1px solid #00ff00;
  color: #00ff00;
  padding: 8px 16px;
  border-radius: 6px;
  text-decoration: none;
  font-size: 12px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: rgba(0, 255, 0, 0.3);
    transform: scale(1.05);
    box-shadow: 0 0 15px rgba(0, 255, 0, 0.4);
  }
`;

const StatsBar = styled.div`
  display: flex;
  justify-content: space-around;
  margin-bottom: 25px;
  background: rgba(0, 255, 0, 0.1);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 8px;
  padding: 15px;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #00ffff;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #cccccc;
`;
const ProjectsExplorer: React.FC = () => {
  const [currentPath] = useState('/home/mukul/projects');

  const projects = [
    {
      name: 'Cyberverse HQ',
      icon: '🕶️',
      description: 'A comprehensive cybersecurity learning platform with interactive modules covering Linux commands, bug bounty vulnerabilities, and career guidance aligned with CEH standards.',
      details: [
        'Developed interactive modules for hands-on learning of Linux commands and cybersecurity concepts',
        'Implemented bug bounty vulnerability training with real-world scenarios',
        'Created career guidance system aligned with CEH (Certified Ethical Hacker) standards',
        'Built immersive 3D cyberpunk-themed interface for enhanced user engagement',
        'Enhanced cybersecurity skills development and career readiness for professionals'
      ],
      tech: ['React', 'Three.js', 'Cybersecurity', 'JavaScript'],
      link: 'https://cyberverse-hq.vercel.app/',
      status: 'Live'
    },
    {
      name: 'CTF Platform',
      icon: '🏁',
      description: 'A comprehensive Capture The Flag platform designed for cybersecurity competitions and training.',
      details: [
        'Developed a robust Capture The Flag platform with user authentication and real-time scoreboards',
        'Implemented multiple challenge categories (Web, Crypto, Forensics) for diverse skill testing',
        'Used Git/GitHub for version control and continuous integration to streamline development'
      ],
      tech: ['Django', 'JavaScript', 'GitHub'],
      link: 'https://ctf-platform-fe9844f01013.herokuapp.com/',
      status: 'Live'
    },
    {
      name: 'CTF Writeups Platform',
      icon: '📝',
      description: 'A blog-style platform for sharing cybersecurity writeups and challenges.',
      details: [
        'Developed a blog-style platform where users can write and share writeups or challenges',
        'Implemented a rich text editor for seamless content creation and formatting',
        'Designed a user-friendly interface with React for easy navigation and readability',
        'Integrated authentication and user profiles, allowing contributors to manage their posts',
        'Optimized performance and ensured secure data storage with Appwrite'
      ],
      tech: ['React', 'Django', 'Appwrite', 'Git'],
      link: 'https://ctfwriteup-ten.vercel.app/',
      status: 'Live'
    },
    {
      name: 'ISDF Club Website',
      icon: '🎲',
      description: 'Interactive 3D website for the Information Security & Digital Forensics Club.',
      details: [
        'Designed and developed a visually complex, interactive 3D rotating cube using Three.js',
        'Implemented hand-controlled rotations and smooth animations for an engaging user experience',
        'Integrated dynamic content on cube faces to showcase club information in an innovative way'
      ],
      tech: ['Three.js', 'JavaScript', 'CSS'],
      link: 'https://isdf-b9b270993f01.herokuapp.com/',
      status: 'Live'
    },
    {
      name: 'Steganography Tool',
      icon: '🔒',
      description: 'A cybersecurity tool for hiding and extracting secret messages in images.',
      details: [
        'Hide your data inside an image and share it securely with other users',
        'Decrypt hidden messages using a generated key from the encryption process',
        'Implements advanced steganography techniques for data concealment',
        'User-friendly interface for both encryption and decryption processes'
      ],
      tech: ['Python', 'Cryptography', 'Image Processing'],
      link: 'https://cyberfy.pythonanywhere.com/',
      status: 'Live'
    },
    {
      name: 'Legacy Portfolio',
      icon: '📄',
      description: 'My previous portfolio website showcasing earlier projects and achievements.',
      details: [
        'Previous version of my portfolio website',
        'Contains historical projects and achievements',
        'Demonstrates progression in web development skills',
        'Archived for reference and comparison'
      ],
      tech: ['HTML', 'CSS', 'JavaScript'],
      link: 'https://cyberfy-3c7995b35425.herokuapp.com/',
      status: 'Archive'
    }
  ];

  const totalProjects = projects.length;
  const liveProjects = projects.filter(p => p.status === 'Live').length;
  const totalTech = new Set(projects.flatMap(p => p.tech)).size;

  return (
    <ExplorerContainer>
      <Toolbar>
        <span>📁</span>
        <PathBar>{currentPath}</PathBar>
        <span>🔍</span>
      </Toolbar>
      
      <ContentArea>
        <StatsBar>
          <StatItem>
            <StatNumber>{totalProjects}</StatNumber>
            <StatLabel>Total Projects</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>{liveProjects}</StatNumber>
            <StatLabel>Live Projects</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>{totalTech}</StatNumber>
            <StatLabel>Technologies</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>100%</StatNumber>
            <StatLabel>Success Rate</StatLabel>
          </StatItem>
        </StatsBar>

        <ProjectGrid>
          {projects.map((project, index) => (
            <ProjectCard key={index}>
              <ProjectIcon>{project.icon}</ProjectIcon>
              <ProjectTitle>{project.name}</ProjectTitle>
              
              <TechStack>
                {project.tech.map((tech, techIndex) => (
                  <TechTag key={techIndex}>{tech}</TechTag>
                ))}
              </TechStack>

              <ProjectDescription>
                <strong>{project.description}</strong>
              </ProjectDescription>

              <DescriptionList>
                {project.details.map((detail, detailIndex) => (
                  <DescriptionItem key={detailIndex}>{detail}</DescriptionItem>
                ))}
              </DescriptionList>

              <ProjectLinks>
                <LinkButton 
                  href={project.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  🌐 Visit Project
                </LinkButton>
                <LinkButton 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    navigator.clipboard.writeText(project.link);
                    alert('Link copied to clipboard!');
                  }}
                >
                  📋 Copy Link
                </LinkButton>
              </ProjectLinks>
            </ProjectCard>
          ))}
        </ProjectGrid>
      </ContentArea>
    </ExplorerContainer>
  );
};

export default ProjectsExplorer;
